import { isString, merge } from "lodash-es";
import { createReactOidc } from "oidc-spa/react";
import { decodeJwt } from "oidc-spa/tools/decodeJwt";
import { LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import { publishUserAction, setupAnalytics } from "./analytics";
import {
  callServiceLight,
  getDefaultContext,
  GetDynamicPage,
  GetSampleDataServicesFromServer,
  GetSiteFromServer,
  GetTemplateFromServer,
  GlobalStateType,
} from "./dataService";
import { log } from "./logger";
import { isNative } from "./native";
import { RuntimeContext } from "./NGFieldExtensions";
import { setupMenu } from "./setupMenu";
import { getProjectTenantConfig, getTenant, getTenantConfig } from "./tenantUtils";
import tracing from "./tracing";
import {
  clearCookiesForImpersonationSessions,
  clearLocalStorageForImpersonationSessions,
  delay,
  getApiUrl,
  getUser,
  isNullOrEmpty,
  parseAuthCookies,
  redirectToMobileApp,
  setCookie,
} from "./utils";

const tag = "auth";

export const {
  OidcProvider,
  /**
   * Note: If you have multiple OidcProvider in your app
   * you do not need to use the useClient hook that that corresponds
   * to the above OidcProvider.
   */
  useOidc,
  /**
   * This is useful to use the oidc API outside of React.
   */
  getOidc,
} = createReactOidc({
  // If you don't have the parameters right away, it's the case for example
  // if you get the oidc parameters from an API you can pass a promise that
  // resolves to the parameters. `createReactOidc(prParams)`.
  // You can also pass an async function that returns the parameters.
  // `createReactOidc(async () => params)`. It will be called when the <OidcProvider />
  // is first mounted or when getOidc() is called.

  // __unsafe_ssoSessionIdleSeconds: 1 * 10,
  issuerUri: `${import.meta.env.VITE_OIDC_PREFIX ?? ""}/auth/realms/${getTenant()}`,
  clientId: import.meta.env.VITE_OIDC_CLIENT,
  homeUrl: import.meta.env.BASE_URL,
  /**
   * This parameter is optional.
   *
   * It allows you to validate the shape of the idToken so that you
   * can trust that oidcTokens.decodedIdToken is of the expected shape
   * when the user is logged in.
   * What is actually inside the idToken is defined by the OIDC server
   * you are using.
   * The usage of zod here is just an example, you can use any other schema
   * validation library or write your own validation function.
   *
   * Note that zod will strip out all the fields that are not defined in the
   * schema, so to know exactly what is inside the idToken you can do:
   * decodedIdTokenSchema: {
   *   parse: (decodedIdToken)=> {
   *     log.error(tag, decodedIdToken);
   *     return z.object({
   *       sub: z.string(),
   *       preferred_username: z.string()
   *     }).parse(decodedIdToken);
   *   }
   * }
   *
   * If you want to specify the type of the decodedIdToken but do not care
   * about validating the shape of the decoded idToken at runtime you can
   * call `createUseOidc<DecodedIdToken>()` without passing any parameter.
   *
   * Note however that in most webapp you do not need to look into the JWT
   * of the idToken on the frontend side, you usually obtain the user info
   * by querying a GET /user endpoint with a authorization header
   * like `Bearer <accessToken>`.
   */
  /*
    decodedIdTokenSchema: {
        parse: (decodedIdToken) => {

            type DecodedIdToken = {
                sub: string;
                preferred_username: string
            };

            log.error(tag, decodedIdToken);

            return decodedIdToken as DecodedIdToken;
        }
    },
    */
  decodedIdTokenSchema: z.object({
    sub: z.string(),
    preferred_username: z.string(),
  }),
  //autoLogoutParams: { redirectTo: "current page" } // Default
  //autoLogoutParams: { redirectTo: "home" }
  //autoLogoutParams: { redirectTo: "specific url", url: "/a-page" }

  // This parameter is optional.
  // It allows you to pass extra query params before redirecting to the OIDC server.
  extraQueryParams: () => ({
    // ui_locales: "en", // Gere you would dynamically get the current language at the time of redirecting to the OIDC server
  }),
  // Remove this in your repo
  debugLogs: true,
});

let tokenChangeUnsubscribe = () => {
  return;
};

export async function performPostLogin(auth, tokens, token, refresh = false) {
  log.info(tag, "performPostLogin", tokens, token);
  const cookies = parseAuthCookies();
  if (cookies["Api.Token"] == tokens.accessToken) {
    log.info(tag, "performPostLogin: tokens are same skipping");
    return;
  }

  const start = performance.now();
  log.info(tag, "performPostLogin", tokens);
  clearCookiesForImpersonationSessions();
  clearLocalStorageForImpersonationSessions();

  const customHeaders = tracing.getHeaders();
  const subject = refresh ? "refresh-login" : "post-login";
  const resp = await fetch(`${getApiUrl()}/auth/${subject}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokens.accessToken}`,
      ...customHeaders,
    } as HeadersInit,
  });
  if (!resp.ok) {
    log.error(tag, "failed to post login", resp);

    log.groupError(tag, `Error: ${resp.status}. Request to ${subject} failed`);
    log.info(
      tag,
      "headers:",
      JSON.stringify({
        "NG-SessionId": customHeaders["NG-SessionId"],
        "NG-RequestId": customHeaders["NG-RequestId"],
        "NG-ActionId": customHeaders["NG-ActionId"],
      })
    );
    log.groupEnd();
    return auth;
  }
  const result = await resp.json();

  const exp = new Date((token.exp ?? new Date().getTime() + 60 * 60) * 1000).toUTCString();
  document.cookie = `Api.Token=${tokens.accessToken}; path=/; expires=${exp}`;
  document.cookie = `Api.NatsJwt=${result.NatsJwt}; path=/; expires=${exp}`;
  document.cookie = `Api.NatsSeed=${result.NatsSeed}; path=/; expires=${exp}`;

  // if impersonating refresh token
  const impersonateId = sessionStorage.getItem("impersonateId") || "";
  if (impersonateId) {
    const refreshToken = localStorage.getItem(`Api.Imp.${impersonateId}.RefreshToken`);
    const userId = localStorage.getItem(`Api.Imp.${impersonateId}.UserId`);
    if (refreshToken) {
      const customHeaders = tracing.getHeaders();
      const refreshResp = await fetch(`${getApiUrl()}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens.accessToken}`,
          ...customHeaders,
        } as HeadersInit,
        body: JSON.stringify({ RefreshToken: refreshToken, UserId: userId }),
      });
      if (refreshResp.ok) {
        const refreshResult = await refreshResp.json();
        localStorage.setItem(`Api.Imp.${impersonateId}.RefreshToken`, refreshResult.RefreshToken);
        localStorage.setItem(`Api.Imp.${impersonateId}.NatsJwt`, refreshResult.NatsJwt);
        localStorage.setItem(`Api.Imp.${impersonateId}.NatsSeed`, refreshResult.NatsSeed);
        localStorage.setItem(`Api.Imp.${impersonateId}.RefreshToken`, refreshResult.RefreshToken);
        localStorage.setItem(`Api.Imp.${impersonateId}.UserId`, refreshResult.UserId);
        setCookie(`Api.Imp.${impersonateId}.Token`, refreshResult.Token, 1);
      } else {
        log.groupError(tag, `Error: ${resp.status}. Request to ${subject} failed`);
        log.info(
          tag,
          "headers:",
          JSON.stringify({
            "NG-SessionId": customHeaders["NG-SessionId"],
            "NG-RequestId": customHeaders["NG-RequestId"],
            "NG-ActionId": customHeaders["NG-ActionId"],
          })
        );
        log.groupEnd();
        return auth;
      }
    }
  }

  const end = performance.now();
  log.info(tag, `performPostLogin took, ${end - start}ms`);
  await delay(100);
}

let setupInProgress: Promise<any> | null = null;

export async function checkForceUpgrade(): Promise<boolean> {
  // we only care about forcing upgrades if the user is using the native app
  if (!isNative()) {
    log.info(tag, "checkForceUpgrade: not native, skipping");
    return false;
  }

  const tenantConfig = getTenantConfig();

  // See if this tenant has a specific URL for force upgrade
  let forceUpgradeUrl = import.meta.env.VITE_FORCE_UPGRADE_MODE_URL;
  if (tenantConfig?.forceUpgradeUrl) {
    log.info(tag, "checkForceUpgrade: client has specific force upgrade URL", tenantConfig.forceUpgradeUrl);
    forceUpgradeUrl = tenantConfig.forceUpgradeUrl;
  }

  try {
    const response = await fetch(forceUpgradeUrl);
    log.info(tag, "isInForceUpgrade response", response);
    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    log.info(tag, "isInForceUpgrade", result);
    if (result.upgrade) {
      window.location.href = `/force-upgrade`;
      //redirectDocument(`/force-upgrade`);
      return true;
    }
    return false;
  } catch (e) {
    log.warn(tag, "Error fetching force upgrade status", e);
    return false;
  }
}

async function checkMaintenance(): Promise<boolean> {
  const tenantConfig = getTenantConfig();

  // See if this tenant has a specific URL for maintenance
  let maintenanceModeUrl = import.meta.env.VITE_MAINTENANCE_MODE_URL;
  if (tenantConfig?.maintenanceModeUrl) {
    log.info(tag, "checkMaintenance: client has specific maintenance URL", tenantConfig.maintenanceModeUrl);
    maintenanceModeUrl = tenantConfig.maintenanceModeUrl;
  }

  try {
    const response = await fetch(maintenanceModeUrl);
    log.info(tag, "isInMaintenance response", response);
    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    log.info(tag, "isInMaintenance", result);
    if (result.maintenance) {
      window.location.href = `/maintenance`;
      //redirectDocument(`/maintenance`);
      return true;
    }
    return false;
  } catch (e) {
    log.warn(tag, "Error fetching maintenance status", e);
    return false;
  }
}

function isValidKeycloakUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Get allowed hosts from current host and environment
    const currentHost = window.location.host;
    const parsedOidcHost = new URL(import.meta.env.VITE_OIDC_HOST);

    const allowedHosts = [currentHost];

    // Add OIDC host if it exists and is different from current host
    if (parsedOidcHost && parsedOidcHost.host !== currentHost) {
      allowedHosts.push(parsedOidcHost.host);
    }

    // Check if the host is in the allowlist
    if (!allowedHosts.includes(parsedUrl.host)) {
      return false;
    }

    // Ensure HTTPS in production (allow HTTP for local development)
    const isLocalhost = parsedUrl.hostname === "localhost" || parsedUrl.hostname === "127.0.0.1";
    if (!isLocalhost && parsedUrl.protocol !== "https:") {
      return false;
    }

    // Optional: Validate common Keycloak paths
    const keycloakPaths = ["/auth/", "/realms/"];

    // Allow if it matches any Keycloak path or if no specific validation needed
    const hasValidPath = keycloakPaths.some((path) => parsedUrl.pathname.startsWith(path));

    return hasValidPath;
  } catch (error) {
    log.warn(tag, "Error parsing URL:", error);
    return false;
  }
}

function checkKcActionUrl() {
  if (isNative()) return;
  const urlParams = new URLSearchParams(window.location.search);
  const kcActionUrl = urlParams.get("kcActionUrl");
  if (kcActionUrl) {
    const decodedUrl = decodeURIComponent(kcActionUrl);
    if (isValidKeycloakUrl(decodedUrl)) {
      window.location.href = decodedUrl;
    } else {
      log.warn(tag, "Invalid Keycloak URL detected:", decodedUrl);
    }
  }
}

async function exchangePublicTokenForPlatformToken(publicToken: string | null, tenant: string): Promise<any> {
  if (!publicToken) return null;
  const url = `${getApiUrl()}/auth/token-exchange`;

  log.debug(tag, "Exchanging public token for platform token", { url, tenant });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Tenant: tenant,
    },
    body: JSON.stringify({
      Token: publicToken,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    log.warn(tag, "Token exchange failed", { status: response.status, error: errorText });
    return null;
  }

  const data = await response.json();
  log.info(tag, "Token exchange successful");
  return data;
}

export async function setupAuth(
  { params, request }: LoaderFunctionArgs,
  global: GlobalStateType,
  context: RuntimeContext
) {
  checkKcActionUrl();
  const redirectTasks = [checkMaintenance(), redirectToMobileApp(), checkForceUpgrade()];
  const result = await Promise.all(redirectTasks);
  if (result.some((r) => r)) {
    return null;
  }

  setupAnalytics();

  // Helper function to publish login action early
  function publishLoginAction(url: string) {
    try {
      const context = getDefaultContext();

      // Extract page details from URL for login action
      const urlObj = new URL(url);
      const urlPath = urlObj.pathname.replace(/^\//, "").replace(/\/$/, ""); // Remove leading/trailing slashes

      // Keep all paths as-is, don't replace slashes
      const pageId = urlPath || "home";
      const pageTitle = urlPath || "home"; // Use full path as title, default to 'home'

      // Create a page config with proper page details for login action
      const pageConfig = {
        __typename: "Page",
        Id: pageId,
        Name: pageTitle,
        Label: pageTitle,
        PageUrl: url,
        PagePath: urlObj.pathname,
        PageReferrer: document.referrer,
        SerialNumber: null,
      };

      // Create minimal context path
      const minimalContext = {
        ...context,
        Path: [{ Config: pageConfig }],
      };

      const action = publishUserAction(pageConfig, minimalContext, "login", null, {
        EventCategory: "auth",
        IsImplicit: true,
      });

      if (action) {
        tracing.setContextAction(action);
        log.info(tag, "Published early login action", action.Id);
      }

      return action;
    } catch (error) {
      log.error(tag, "Failed to publish early login action", error);
      return null;
    }
  }

  async function runSetup(token) {
    await setupUser(decodeJwt(token), global, context);
    if (!global["NGSite"]?.value) {
      await GetSiteFromServer(context);
    }
    await GetTemplateFromServer(context);
    await GetSampleDataServicesFromServer(context);
  }

  const cookies = parseAuthCookies();
  const ApiToken = cookies["Api.Token"];

  if (isNative()) {
    if (!isNullOrEmpty(ApiToken) && !setupInProgress) {
      setupInProgress = (async () => {
        // For native apps, check if this is a new session and publish login action
        const sessionId = tracing.getSessionId();
        if (!sessionId) {
          tracing.createSessionId();
        }

        await runSetup(ApiToken);
        if (tracing.newSessionCreated) {
          tracing.newSessionCreated = false;
          publishLoginAction(request.url);
        }
      })();
    }
    return {
      isUserLoggedIn: !isNullOrEmpty(ApiToken),
    };
  }

  const auth = await getOidc();

  if ((auth as any).initializationError) {
    log.error(tag, "OIDC init error", (auth as any).initializationError);
    return auth;
  }

  if (auth.isUserLoggedIn) {
    if (!setupInProgress) {
      setupInProgress = (async () => {
        if (tokenChangeUnsubscribe) {
          tokenChangeUnsubscribe();
        }
        const { unsubscribe } = auth.subscribeToTokensChange(async () => {
          const tokens = auth.getTokens();
          const token = decodeJwt(tokens.accessToken);
          const maintenanceResult = await checkMaintenance();
          if (maintenanceResult) return;
          await performPostLogin(auth, tokens, token, true);
        });
        tokenChangeUnsubscribe = unsubscribe;

        const tokens = auth.getTokens();
        const token = decodeJwt(tokens.accessToken);
        if (auth.isNewBrowserSession || !ApiToken) {
          const sessionId = tracing.getSessionId();
          if (!ApiToken || !sessionId) {
            // If the session ID is not set, or ApiToken is not set, user just logged in, we need to create a new session ID
            tracing.createSessionId();
          }
          await performPostLogin(auth, tokens, token);
        }

        if (!global["User"]?.value) {
          const cookies = parseAuthCookies();
          await runSetup(cookies["Api.Token"]);
          if (tracing.newSessionCreated) {
            tracing.newSessionCreated = false;
            publishLoginAction(request.url);
          }
        }
      })();
    }

    await setupInProgress;

    return auth;
  }
  // If the user is not logged in, we need to clear the session ID
  tracing.clearSessionId();

  const tenantConfig = getTenantConfig();
  const publicToken = tenantConfig?.publicToken;
  const tokenData = await exchangePublicTokenForPlatformToken(publicToken ?? null, "");

  if (publicToken && tokenData) {
    log.info(tag, "Public token detected, initiating token exchange");
    log.debug(tag, "Token data received", { hasToken: !!tokenData.Token, hasNatsJwt: !!tokenData.NatsJwt });

    const decodedToken = decodeJwt(tokenData.Token);
    const exp = new Date((decodedToken.exp ?? new Date().getTime() + 60 * 60) * 1000).toUTCString();

    // Store all three cookies (like performPostLogin does)
    log.info(tag, "Storing authentication cookies for public token access");
    document.cookie = `Api.Token=${tokenData.Token}; path=/; expires=${exp}`;
    document.cookie = `Api.NatsJwt=${tokenData.NatsJwt}; path=/; expires=${exp}`;
    document.cookie = `Api.NatsSeed=${tokenData.NatsSeed}; path=/; expires=${exp}`;

    // Set up user and context
    if (!setupInProgress) {
      setupInProgress = (async () => {
        await runSetup(tokenData.Token);
      })();
    }
    await setupInProgress;

    let foundPage: any = null;

    // Now try to fetch the page
    try {
      log.info(tag, "Fetching dynamic page", { routeId: params.routeId });
      foundPage = await GetDynamicPage(params.routeId, context);
      log.info(tag, "Dynamic page fetched successfully", { pageId: foundPage?.Id, isPublic: foundPage?.IsPublic });
    } catch (error) {
      log.error(tag, "Failed to fetch dynamic page", error);
    }

    const isPublicPage = foundPage?.IsPublic ?? false;

    if (isPublicPage) {
      log.info(tag, "Page is public, returning authenticated status with page data");

      return {
        isUserLoggedIn: true,
        page: foundPage,
      };
    } else {
      log.info(tag, "Page is not public, clearing cookies and redirecting to login");
      // clear cookies and let the auth.login take over
      document.cookie = `Api.Token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `Api.NatsJwt=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `Api.NatsSeed=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  await auth.login({
    // The loader function is invoked by react-router before the browser URL is updated to the target protected route URL.
    // Therefore, we need to specify where the user should be redirected after the login process completes.
    redirectUrl: request.url,

    // Explanation:
    // The 'doesCurrentHrefRequiresAuth' parameter informs oidc-spa whether it is acceptable to redirect the user to the current URL
    // if the user abandons the authentication process. This is crucial to prevent the user from being immediately redirected
    // back to the login page when pressing the back button from the login pages.
    // If the user navigated directly to the protected route (e.g., by clicking a link to your application from an external site),
    // then the current URL requires authentication.
    // Conversely, if the user navigated from an unprotected route within your application to the protected route,
    // then the current URL does not require authentication.
    doesCurrentHrefRequiresAuth: window.location.href === request.url,
  });

  // Never here, the login method redirects the user to the identity provider.
}

async function setupUser(token, global: GlobalStateType, context: RuntimeContext) {
  if (!token) return null;
  const user = getUser(isString(token) ? decodeJwt(token) : token);
  log.info(tag, "~~User: got user", user);
  const setupPromises = [getUserProfile(user.Id, context), getProjectTenantConfig(context)];
  const [profile] = (await Promise.allSettled(setupPromises)).map((r) => (r.status === "fulfilled" ? r : null));

  const permissionTask = getPermissions(context);
  const fullUser = merge(user, profile?.value?.data || {});
  log.info(tag, "~~User: updating user with profile", global["User"], fullUser);

  if (isNative() && window.NativeApp && profile?.value?.data) {
    const appVersion = { [`${window.NativeApp?.Platform}Version`]: window.NativeApp?.Version };
    updateUserProfileDetails(fullUser.Id, { ...fullUser.Details, ...appVersion }, context);
  }

  global["User"].value = fullUser;

  setupMenu(fullUser, context);

  (async () => {
    const permissions = await permissionTask;
    if (!permissions.success) {
      log.error(tag, "~~User: failed to get permissions", profile?.value.data, permissions.data);
      return token;
    }
    global["Permissions"].value = permissions.data;
  })();
  return token;
}

export async function getUserProfile(id: string, context: RuntimeContext) {
  return callServiceLight(
    { Name: "UserProfileGet", Service: "userprofile.get", IgnoreLocale: true },
    { Id: id },
    context
  );
}

export async function getOpenapiQuery(context: RuntimeContext) {
  return callServiceLight({ Name: "OpenAPi", Service: "openapi.query", IgnoreLocale: true }, {}, context);
}

export async function updateUserProfileDetails(id: string, details: any, context: RuntimeContext) {
  log.info(tag, "updateUserProfileDetails", id, details, context);
  return callServiceLight(
    { Name: "UserProfileUpdate", Service: "userprofile.update", IgnoreLocale: true, Debounce: 3000 },
    { Id: id, Details: details },
    context
  );
}

async function getPermissions(context: RuntimeContext) {
  return callServiceLight({ Name: "Permission", Service: "permission.get", IgnoreLocale: true }, {}, context);
}
